<script setup lang="ts">
import { Play, Pause, SkipBack, SkipForward, RotateCcw } from "@lucide/vue";
import LayerMenu from "./LayerMenu.vue";
import type { RadarSource, RadarTimestamp } from "@/utils/radarProvider";
import { Button } from "@/components/ui/button";

defineProps<{
  timestamps: RadarTimestamp[];
  currentFrameIndex: number;
  isPlaying: boolean;
  timeDisplay: string;
  progressPercent: number;
  // Layer menu props (passed through)
  showRadarLayer: boolean;
  radarOpacity: number;
  isWindLayerActive: boolean;
  windParticleOpacity: number;
  radarSourceOverride?: RadarSource;
  activeRadarSource: RadarSource;
}>();

const emit = defineEmits<{
  "settings-open-change": [open: boolean];
  "prev-frame": [];
  "play-pause": [];
  "next-frame": [];
  "go-to-latest": [];
  // Layer menu emits (passed through)
  "toggle-radar": [];
  "toggle-wind": [];
  "update-radar-opacity": [value: number];
  "update-wind-opacity": [value: number];
  "update-radar-source": [source: RadarSource | undefined];
}>();

</script>

<template>
  <div class="flex items-center gap-2">
    <div
      class="flex max-w-[calc(100%-24px)] min-w-[280px] items-center rounded-panel border border-app-border bg-app-material px-3.5 py-1.5"
    >
      <div class="flex items-center gap-2">
        <!-- Transport buttons -->
        <Button
          variant="control"
          size="icon-compact"
          aria-label="Previous radar frame"
          @click="emit('prev-frame')"
          :disabled="timestamps.length === 0"
        >
          <SkipBack class="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="control"
          size="icon-compact"
          :aria-label="isPlaying ? 'Pause radar' : 'Play radar'"
          @click="emit('play-pause')"
          :disabled="timestamps.length === 0"
          class="border-app-border"
        >
          <Pause v-if="isPlaying" class="h-3.5 w-3.5" />
          <Play v-else class="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="control"
          size="icon-compact"
          aria-label="Next radar frame"
          @click="emit('next-frame')"
          :disabled="timestamps.length === 0"
        >
          <SkipForward class="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="control"
          size="icon-compact"
          @click="emit('go-to-latest')"
          :disabled="timestamps.length === 0"
          title="Reset to latest"
        >
          <RotateCcw class="h-3.5 w-3.5" />
        </Button>

        <!-- Progress bar -->
        <div
          class="relative mx-1 h-1 min-w-[60px] flex-1 rounded-full bg-app-selected"
        >
          <div
            class="absolute inset-y-0 left-0 rounded-full bg-blue-500 transition-all duration-300"
            :style="{ width: progressPercent + '%' }"
          />
        </div>

        <!-- Frame counter + time -->
        <span class="font-mono text-[10px] whitespace-nowrap text-text-muted">
          {{ Math.min(currentFrameIndex + 1, timestamps.length) }}/{{ timestamps.length }}
        </span>
        <span class="font-mono text-[10px] whitespace-nowrap text-text-muted">
          {{ timeDisplay }}
        </span>

        <!-- Layer settings gear -->
        <LayerMenu
          class="w-12"
          :show-radar-layer="showRadarLayer"
          :radar-opacity="radarOpacity"
          :is-wind-layer-active="isWindLayerActive"
          :wind-particle-opacity="windParticleOpacity"
          :radar-source-override="radarSourceOverride"
          :active-radar-source="activeRadarSource"
          :radar-frame-count="timestamps.length"
          @open-change="emit('settings-open-change', $event)"
          @toggle-radar="emit('toggle-radar')"
          @toggle-wind="emit('toggle-wind')"
          @update-radar-opacity="emit('update-radar-opacity', $event)"
          @update-wind-opacity="emit('update-wind-opacity', $event)"
          @update-radar-source="emit('update-radar-source', $event)"
        />
      </div>
    </div>
  </div>
</template>
